import { useState, useRef } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Paperclip, X, Forward as ForwardIcon, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import EmailChipsInput from "./EmailChipsInput";

const MAX_TOTAL_BYTES = 25 * 1024 * 1024;

function fmtSize(n) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Forward a single message to one or more new recipients.
 */
export default function ForwardDialog({ open, onOpenChange, message, onSent }) {
  const [recipients, setRecipients] = useState([]); // chip-based
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [gmailTokenExpired, setGmailTokenExpired] = useState(false);
  const fileInputRef = useRef(null);

  const reset = () => {
    setRecipients([]);
    setNote("");
    setFiles([]);
    setGmailTokenExpired(false);
  };

  const onClose = (v) => {
    if (!v && !sending) reset();
    onOpenChange(v);
  };

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const next = [...files, ...picked];
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_BYTES) {
      toast.error("Attachments exceed 25 MB (Gmail limit)");
      return;
    }
    setFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSend = async () => {
    if (recipients.length === 0) {
      toast.error("Add at least one recipient");
      return;
    }
    if (!message?.id) return;
    setSending(true);
    setGmailTokenExpired(false);
    try {
      const toCsv = recipients.join(", ");
      if (files.length > 0) {
        const fd = new FormData();
        fd.append("to", toCsv);
        fd.append("body_text", note);
        for (const f of files) fd.append("files", f, f.name);
        await api.post(`/messages/${message.id}/forward`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await api.post(`/messages/${message.id}/forward`, {
          to: toCsv,
          body_text: note,
        });
      }
      toast.success(`Forwarded to ${recipients.length} recipient${recipients.length === 1 ? "" : "s"}`);
      reset();
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Forward failed";
      // Surface a clearer message when Google has revoked the OAuth token.
      if (typeof detail === "string" && /invalid_grant|token has been expired|token has been revoked/i.test(detail)) {
        setGmailTokenExpired(true);
        toast.error("Your Gmail connection has expired — reconnect from Admin → Inboxes.");
      } else {
        toast.error(detail);
      }
    } finally {
      setSending(false);
    }
  };

  const origAttCount = (message?.attachments || []).length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="forward-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base flex items-center gap-2">
            <ForwardIcon className="w-4 h-4 text-[#0033CC]" strokeWidth={2} />
            Forward message
          </DialogTitle>
        </DialogHeader>

        {message && (
          <div className="text-xs text-zinc-600 bg-zinc-50 border border-zinc-200 rounded-md p-2.5 space-y-1" data-testid="forward-source-summary">
            <div className="truncate"><span className="text-zinc-400">From:</span> {message.from_name || message.from_email}</div>
            <div className="truncate"><span className="text-zinc-400">Subject:</span> {message.subject || "(no subject)"}</div>
            {origAttCount > 0 && (
              <div className="text-zinc-500 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" strokeWidth={1.75} />
                {origAttCount} original attachment{origAttCount === 1 ? "" : "s"} will be included
              </div>
            )}
          </div>
        )}

        {gmailTokenExpired && (
          <div
            className="text-xs bg-amber-50 border border-amber-300 text-amber-900 rounded-md p-3 flex items-start gap-2"
            data-testid="forward-gmail-expired"
          >
            <AlertTriangle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <div className="font-semibold mb-0.5">Gmail connection expired</div>
              <p className="leading-snug">
                Google revoked the OAuth token for this inbox. Open <span className="font-medium">Admin → Inboxes</span>, disconnect, and reconnect to authorize again. Then come back here and resend.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-semibold tracking-[0.05em] uppercase text-zinc-500">To</label>
            <div className="mt-1">
              <EmailChipsInput
                value={recipients}
                onChange={setRecipients}
                placeholder="name@example.com"
                testIdPrefix="forward-to"
              />
            </div>
            <p className="text-[10px] text-zinc-500 mt-1">Press Enter, space, or comma to add. Paste a list to add many at once.</p>
          </div>

          <div>
            <label className="text-[10px] font-semibold tracking-[0.05em] uppercase text-zinc-500">Note (optional)</label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a short note above the forwarded message…"
              className="mt-1 min-h-[80px] rounded-md border-zinc-300 focus-visible:ring-[#0033CC] text-sm"
              data-testid="forward-note-input"
            />
          </div>

          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 text-xs text-[#0033CC] hover:underline"
              data-testid="forward-attach-button"
            >
              <Paperclip className="w-3.5 h-3.5" strokeWidth={1.75} />
              Add extra files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={onPickFiles}
              className="hidden"
              data-testid="forward-file-input"
            />
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="forward-extra-files">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1.5 bg-zinc-100 border border-zinc-200 rounded-md pl-2 pr-1 py-1 text-xs"
                  >
                    <Paperclip className="w-3 h-3 text-zinc-500" strokeWidth={1.75} />
                    <span className="truncate max-w-[180px]">{f.name}</span>
                    <span className="text-zinc-500">· {fmtSize(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="ml-0.5 p-0.5 text-zinc-500 hover:text-red-600 hover:bg-white rounded"
                      title="Remove"
                      data-testid={`forward-remove-extra-${i}`}
                    >
                      <X className="w-3 h-3" strokeWidth={2} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onClose(false)}
            disabled={sending}
            data-testid="forward-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={onSend}
            disabled={sending || recipients.length === 0}
            className="bg-[#0033CC] hover:bg-[#002299] text-white"
            data-testid="forward-send-button"
          >
            <ForwardIcon className="w-4 h-4 mr-1.5" strokeWidth={2} />
            {sending ? "Sending…" : `Forward${recipients.length > 1 ? ` to ${recipients.length}` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
